import React, { useState } from 'react';
import { Alert, Spin } from 'antd';

interface AssetImageViewerProps {
    // Absolute (typically presigned) URL to the image.
    url: string;
    alt?: string;
    // Height budget; the image is scaled to fit inside this box (preserving
    // aspect ratio). Defaults to 75vh, which fits on a desktop / iPad with no
    // scrolling. A caller may pass a value above 100vh to render the sheet
    // larger, accepting that a tall image then scrolls within the page.
    maxHeight?: number | string;
    'data-testid'?: string;
}

// Renders an image scaled to fit its container (width and height) with no
// scrolling. A plain <img> — so it renders everywhere (incl. iOS Safari) and,
// unlike a fetch/canvas approach, needs no CORS on the asset's origin.
export const AssetImageViewer: React.FC<AssetImageViewerProps> = ({
    url,
    alt = 'Pricing sheet',
    maxHeight = '75vh',
    'data-testid': testId,
}) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    return (
        <div
            data-testid={testId}
            style={{
                width: '100%',
                maxHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                overflow: 'hidden',
                minHeight: 120,
            }}
        >
            {error ? (
                <Alert
                    type="error"
                    showIcon
                    message="Couldn't display the document"
                    description={
                        <a href={url} target="_blank" rel="noopener noreferrer">Open in a new tab</a>
                    }
                    data-testid="asset-image-error"
                />
            ) : (
                <>
                    {!loaded && <Spin size="large" data-testid="asset-image-loading" />}
                    <img
                        src={url}
                        alt={alt}
                        data-testid="asset-image"
                        onLoad={() => setLoaded(true)}
                        onError={() => setError(true)}
                        // maxWidth/maxHeight do the fitting: the image scales down
                        // to fit the box in both dimensions, preserving aspect
                        // ratio, so the whole sheet shows with no scrolling.
                        style={{
                            display: loaded ? 'block' : 'none',
                            maxWidth: '100%',
                            maxHeight,
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default AssetImageViewer;
